import React, { useEffect, useRef } from 'react';
import { getVideoSources, intersect, renderMosaic, updateMosaicFallback } from './video-mosaic';

export default function VideoMosaic({ mosaics, meetingFrameRef }) {
  const overlayRef = useRef(null);
  const canvasRefs = useRef(new Map());
  useEffect(() => {
    const buffers = new Map();
    let animation;
    let lastFrame = -Infinity;
    const draw = time => {
      if (time - lastFrame >= 1000 / 15) {
        lastFrame = time;
        const rect = overlayRef.current.getBoundingClientRect();
        const frame = meetingFrameRef.current?.querySelector('iframe');
        const sources = getVideoSources(frame, rect);
        for (const m of mosaics) {
          const canvas = canvasRefs.current.get(m.id);
          if (!canvas) continue;
          if (!buffers.has(m.id)) buffers.set(m.id, [document.createElement('canvas'), document.createElement('canvas')]);
          const [sample, blurred] = buffers.get(m.id);
          const bounds = {
            ...m, left: m.left * rect.width / 100, top: m.top * rect.height / 100,
            width: m.width * rect.width / 100, height: m.height * rect.height / 100
          };
          // Allocate only the visible portion, even for large zoomed shapes.
          // The outer element clips the original ellipse without distorting it.
          const visible = intersect(bounds, { left: 0, top: 0, width: rect.width, height: rect.height });
          canvas.style.left = `${visible.left - bounds.left}px`;
          canvas.style.top = `${visible.top - bounds.top}px`;
          canvas.style.width = `${visible.width}px`;
          canvas.style.height = `${visible.height}px`;
          const covered = renderMosaic(canvas, sample, blurred, sources, { ...visible, type: 'rect', lowResolution: true });
          updateMosaicFallback(canvas, covered);
        }
      }
      animation = requestAnimationFrame(draw);
    };
    animation = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(animation);
  }, [mosaics, meetingFrameRef]);

  return <div ref={overlayRef} className="video-mosaic-overlay"
    style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1, overflow: 'hidden' }}>
    {mosaics.map(m => <div key={m.id} style={{ position: 'absolute', left: `${m.left}%`, top: `${m.top}%`,
      width: `${m.width}%`, height: `${m.height}%`, overflow: 'hidden',
      borderRadius: m.type === 'ellipse' ? '50%' : 0,
      backdropFilter: 'blur(28px)', WebkitBackdropFilter: 'blur(28px)' }}>
      <canvas ref={node => node ? canvasRefs.current.set(m.id, node) : canvasRefs.current.delete(m.id)}
        style={{ display: 'block', position: 'absolute', width: '100%', height: '100%', imageRendering: 'pixelated' }} aria-hidden="true" />
    </div>)}
  </div>;
}
